// Shared formatting for the AI trace views (runs panel, timeline overlay).
// Pure helpers: no React, no network. Every label goes through the caller's t().
import { formatShortDateTime, formatShortTime } from '../../utils/dates';

export const fmtMs = (ms) => {
    if (typeof ms !== 'number') return '—';
    if (ms < 1000) return `${Math.round(ms)} ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
    return `${Math.floor(ms / 60000)} min ${Math.round((ms % 60000) / 1000)} s`;
};

export const fmtCost = (usd) => (typeof usd === 'number' ? `$${usd.toFixed(usd < 0.01 ? 4 : 3)}` : null);

// Dates: the shared short format (utils/dates — parseUtc: run and span
// timestamps are naive UTC, KI-110; Paris time). A trace reads by the second.
// « 14:05:09 »
export const fmtClock = (iso, lang) => formatShortTime(iso, lang, { seconds: true, empty: '' });

// « 28 oct., 14:05:09 »
export const fmtDateTime = (iso, lang) => formatShortDateTime(iso, lang, { seconds: true });

export const shortName = (span) => {
    const name = span?.name || '';
    const idx = name.indexOf('.');
    return idx >= 0 ? name.slice(idx + 1) : name;
};

export const compactJson = (value, max = 160) => {
    if (value === null || value === undefined) return '';
    const text = typeof value === 'string' ? value : JSON.stringify(value);
    return text.length > max ? `${text.slice(0, max)}…` : text;
};

export const prettyPayload = (text) => {
    try {
        return JSON.stringify(JSON.parse(text), null, 2);
    } catch (e) {
        return text;
    }
};

export const statusClass = (status) => {
    if (status === 'error' || status === 'failed') return 'error';
    if (status === 'expected_error' || status === 'abandoned' || status === 'partial') return 'warn';
    if (status === 'running') return 'running';
    return 'ok';
};

// One line describing what a span did, from its attributes (never a payload body).
export const spanHeadline = (span, t, lang) => {
    const attrs = span?.attrs || {};
    switch (span?.kind) {
        case 'llm': {
            const model = attrs.model_served || attrs.model_requested;
            const usage = attrs.usage || {};
            const tokens = (usage.input_tokens || 0) + (usage.output_tokens || 0);
            const parts = [model, tokens ? `${tokens.toLocaleString(lang)} ${t('aiRunPanel.counters.tokens', 'tokens')}` : null, fmtCost(attrs.cost_usd)];
            const calls = Array.isArray(attrs.tool_calls) ? attrs.tool_calls.map((c) => c.name).filter(Boolean) : [];
            if (calls.length) parts.push(`→ ${calls.join(', ')}`);
            return parts.filter(Boolean).join(' · ');
        }
        case 'tool':
            return [
                attrs.tool_effect ? t(`aiTimeline.toolEffect.${attrs.tool_effect}`, attrs.tool_effect) : null,
                attrs.tool_policy && attrs.tool_policy !== 'auto' ? t(`aiTimeline.toolPolicy.${attrs.tool_policy}`, attrs.tool_policy) : null,
                attrs.tool_source && attrs.tool_source !== 'platform' ? attrs.tool_source : null,
                attrs.result_class,
                compactJson(attrs.args_redacted, 120),
            ].filter(Boolean).join(' · ');
        case 'decision': {
            const outcome = attrs.decision?.outcome;
            if (attrs.decision?.agent === 'enrichment' && outcome && Array.isArray(outcome.sources)) {
                // The enrichment step of the ticket workflow: which sources ran, which were skipped and why.
                const ran = outcome.sources.filter((s) => s.action === 'run');
                const skipped = outcome.sources.filter((s) => s.action !== 'run');
                const parts = [outcome.found ? `${t('aiTimeline.enrichment.foundBy', 'trouvé par')} ${outcome.found_by}` : t('aiTimeline.enrichment.notFound', 'rien trouvé')];
                if (ran.length) parts.push(`${ran.map((s) => `${s.key}${s.ms ? ` ${s.ms} ms` : ''}`).join(', ')}`);
                if (skipped.length) parts.push(`${t('aiTimeline.enrichment.skipped', 'ignorées')} : ${skipped.map((s) => `${s.key} (${t(`aiTimeline.enrichment.reason.${String(s.reason || '').split(':')[0]}`, s.reason || s.action)})`).join(', ')}`);
                if (outcome.not_found_action && outcome.not_found_action !== 'continue') parts.push(t(`aiTimeline.enrichment.action.${outcome.not_found_action}`, outcome.not_found_action));
                if (!outcome.workflow) parts.push(t('aiTimeline.enrichment.implicit', 'parcours par défaut'));
                return parts.join(' · ');
            }
            if (attrs.decision?.agent === 'intake_filter' && outcome && outcome.reason) {
                // A skipped inbound item (backend plan T): the typed reason and the stage.
                return [t(`aiTimeline.skip.${outcome.reason}`, outcome.reason), outcome.stage, outcome.status ? `${t('aiTimeline.skip.status', 'statut')} ${outcome.status}` : null,
                    outcome.carrier || null, outcome.detail ? compactJson(outcome.detail, 100) : null].filter(Boolean).join(' · ');
            }
            if (attrs.decision?.agent === 'context_builder' && outcome && Array.isArray(outcome.sections)) {
                const kept = outcome.sections.filter((s) => !s.dropped && !s.empty).length;
                const flags = [];
                if (outcome.truncated?.length) flags.push(`${outcome.truncated.length} ${t('aiTimeline.context.truncated', 'tronquée(s)')}`);
                if (outcome.dropped?.length) flags.push(`${outcome.dropped.length} ${t('aiTimeline.context.dropped', 'retirée(s)')}`);
                if (outcome.history?.dropped_items) flags.push(`${outcome.history.dropped_items} ${t('aiTimeline.context.historyDropped', 'tours compactés')}`);
                if (outcome.over_budget) flags.push(t('aiTimeline.context.overBudget', 'hors budget'));
                return [`${kept} ${t('aiTimeline.context.sections', 'sections')}`, `${(outcome.total_tokens || 0).toLocaleString(lang)}${outcome.budget ? ` / ${outcome.budget.toLocaleString(lang)}` : ''} ${t('aiRunPanel.counters.tokens', 'tokens')}`, ...flags].join(' · ');
            }
            return compactJson(outcome, 160);
        }
        case 'effect': {
            const effect = attrs.effect || {};
            const value = ['tags', 'language', 'subject', 'sentiment_score', 'topic', 'assistant_id', 'channel', 'count']
                .map((k) => (effect[k] !== undefined && effect[k] !== null ? `${k}: ${compactJson(effect[k], 60)}` : null))
                .filter(Boolean);
            const target = effect.target ? compactJson(effect.target, 60) : '';
            return [effect.outcome, effect.via, effect.after?.status ? `→ ${effect.after.status}` : null, ...value, target].filter(Boolean).join(' · ');
        }
        case 'feedback': {
            const fb = attrs.feedback || {};
            const diff = typeof fb.diff_ratio === 'number' ? `${Math.round(fb.diff_ratio * 100)} % ${t('aiRunPanel.changed', 'modifié')}` : null;
            return [fb.actor?.type, diff].filter(Boolean).join(' · ');
        }
        case 'external': {
            const ext = attrs.external || {};
            if (ext.system === 'portal') {
                const portal = attrs.portal || {};
                return [portal.url, typeof portal.since_previous_ms === 'number' ? `+${fmtMs(portal.since_previous_ms)}` : null,
                    portal.screenshot_s3_key ? t('aiTimeline.capture', 'capture') : null].filter(Boolean).join(' · ');
            }
            return [ext.system, ext.operation, ext.ref, attrs.http_status ? `HTTP ${attrs.http_status}` : null,
                typeof attrs.bytes === 'number' ? `${attrs.bytes} o` : null].filter(Boolean).join(' · ');
        }
        case 'automation': {
            const auto = attrs.automation || {};
            return [auto.outcome, auto.failure_category, auto.error ? compactJson(auto.error, 100) : null].filter(Boolean).join(' · ');
        }
        default:
            return '';
    }
};

// A human title for a span card: the effect type, the tool name, the agent...
export const spanTitle = (span, t) => {
    const attrs = span?.attrs || {};
    switch (span?.kind) {
        case 'llm':
            return t(`aiTimeline.llm.${span.operation || 'call'}`, span.operation || t('aiRunPanel.kind.llm', 'modèle'));
        case 'tool':
            return (attrs.tool?.name || shortName(span)).replace(/_/g, ' ');
        case 'decision':
            return t(`aiTimeline.decision.${attrs.decision?.agent || shortName(span)}`, (attrs.decision?.agent || shortName(span)).replace(/_/g, ' '));
        case 'effect':
            return t(`aiTimeline.effect.${attrs.effect?.type || shortName(span)}`, (attrs.effect?.type || shortName(span)).replace(/_/g, ' '));
        case 'feedback':
            return t(`aiTimeline.feedback.${attrs.feedback?.type || shortName(span)}`, (attrs.feedback?.type || shortName(span)).replace(/_/g, ' '));
        case 'external':
            if (attrs.external?.system === 'portal') {
                return t(`aiTimeline.portal.${attrs.external?.operation}`, `${t('aiTimeline.portal.step', 'Portail')} · ${(attrs.external?.operation || '').replace(/_/g, ' ')}`);
            }
            return `${attrs.external?.system || ''} · ${attrs.external?.operation || shortName(span)}`.replace(/_/g, ' ');
        case 'automation':
            return t(`aiTimeline.automation.${attrs.automation?.outcome || shortName(span)}`, `${t('aiRunPanel.kind.automation', 'automatisation')} · ${attrs.automation?.outcome || shortName(span)}`);
        default:
            return shortName(span);
    }
};

export const runTitle = (run, t) => {
    const kind = run?.trigger?.kind || '';
    const source = run?.trigger?.source || '';
    return `${t(`aiRunPanel.trigger.${kind}`, kind)} · ${source}`;
};

// ----------------------------------------------------------------------
// The ticket window humanizes the agent's tool calls (UI rework 2026-10):
// a French verb for the frequent tools, the tool name spelled out otherwise.
// The raw arguments / result stay one disclosure away.
// ----------------------------------------------------------------------

const TOOL_LABELS = {
    read_ticket_file: 'Lire une pièce jointe',
    update_ticket_details: 'Mettre à jour le ticket',
    attach_ticket_files: 'Joindre des fichiers',
    refresh_ticket_tracking: 'Actualiser le suivi',
    change_ticket_status: 'Changer le statut',
    assign_to_employee: 'Assigner à un membre de l’équipe',
    close_non_request: 'Clore (pas une demande)',
    resolve_ticket: 'Résoudre le ticket',
    pass_response: 'Ne pas répondre',
    contact_carrier: 'Contacter le transporteur',
    carriers_conversation: 'Contacter le transporteur',
    update_carrier_conversation: 'Mettre à jour la demande transporteur',
    correct_carrier_request: 'Corriger la demande transporteur',
    fetch_order_and_tracking_data: 'Chercher la commande et le suivi',
    get_tracking_info: 'Consulter le suivi',
    retrieve_knowledge: 'Consulter la base de connaissances',
    modify_shipping_address_order: 'Modifier l’adresse de livraison',
    find_relay_points: 'Chercher des points relais',
    create_return_with_edn_label: 'Créer un retour avec étiquette',
    save_order_id: 'Enregistrer le n° de commande',
    contact_warehouse: 'Contacter l’entrepôt',
    record_refund: 'Enregistrer un remboursement',
};

const spellOut = (name) => {
    const text = String(name || '').replace(/[_.]+/g, ' ').trim();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};

// « Contacter le transporteur » for contact_carrier; « Some tool » otherwise.
export const toolLabel = (name, t) => {
    if (!name) return '';
    const fallback = TOOL_LABELS[name] || spellOut(name);
    return t ? t(`ticketWorkspaceUi.tools.${name}`, fallback) : fallback;
};

// The parsed body of a tool result (JSON, or the Python repr the agent logs
// stores with single quotes), or null.
export const parseToolResult = (raw) => {
    if (raw === null || raw === undefined || raw === '') return null;
    if (typeof raw === 'object') return raw;
    try {
        return JSON.parse(raw);
    } catch (e) {
        try {
            return JSON.parse(String(raw).replace(/'/g, '"'));
        } catch (e2) {
            return null;
        }
    }
};

// ok | error | other (an unknown status word is shown as is).
export const toolOutcome = (raw) => {
    const parsed = parseToolResult(raw);
    const status = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed.status : null;
    if (!status || status === 'success' || status === 'completed' || status === 'ok') return { kind: 'ok', status: status || 'completed' };
    if (status === 'error' || status === 'failed') return { kind: 'error', status };
    return { kind: 'other', status: String(status) };
};

// A JSON string or value, indented for a <pre>; the text itself when it is not JSON.
export const prettyJson = (value) => {
    if (value === null || value === undefined) return '';
    const parsed = typeof value === 'string' ? parseToolResult(value) : value;
    if (parsed === null) return String(value);
    try {
        return JSON.stringify(parsed, null, 2);
    } catch (e) {
        return String(value);
    }
};

// French wording of a run / step status when the locale has none.
const RUN_STATUS_LABELS = {
    ok: 'réussi',
    error: 'erreur',
    failed: 'échec',
    expected_error: 'erreur attendue',
    abandoned: 'abandonné',
    partial: 'partiel',
    running: 'en cours',
};

export const runStatusLabel = (status, t) => t(`aiRunPanel.status.${status}`, RUN_STATUS_LABELS[status] || status || '');
